/* INKDnART Premium Motion layer – vanilla, no dependencies */
(() => {
  const body = document.body;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motion = !reduced && !body.classList.contains('motion-off');
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  if (motion) body.classList.add('fx-motion');

  /* ---------- section decor: gradients + faint INKDnART branding ---------- */
  const SEC = '.sec, .hero, .cta, .coll-hero';
  const visible = new Set();
  const vio = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(en => en.isIntersecting ? visible.add(en.target) : visible.delete(en.target)), { rootMargin: '120px 0px' }) : null;
  let secIndex = 0;
  function decorate(scope = document) {
    if (!body.classList.contains('fx-bg') && !body.classList.contains('fx-brand')) return;
    $$(SEC, scope).forEach(sec => {
      if (sec.dataset.fx || sec.querySelector('.fdrop, [data-pdp]') || sec.closest('.drawer, .modal, .hdr, .ftr')) return;
      sec.dataset.fx = 1; sec.classList.add('fx-sec');
      const d = document.createElement('div');
      d.className = 'fx-decor'; d.setAttribute('aria-hidden', 'true');
      d.dataset.side = (secIndex++ % 2) ? 'r' : 'l';
      d.innerHTML = '<span class="fx-word">' + (body.dataset.fxWord || 'INKDnART') + '</span><i class="fx-emblem"></i>';
      sec.prepend(d);
      vio && vio.observe(sec);
    });
  }

  /* ---------- split hero headline into animated characters ---------- */
  function splitHero(scope = document) {
    if (!motion) return;
    $$('.hero__line', scope).forEach(line => {
      if (line.dataset.split) return; line.dataset.split = 1;
      const h1 = line.closest('h1');
      if (h1 && !h1.hasAttribute('aria-label')) h1.setAttribute('aria-label', h1.textContent.replace(/\s+/g, ' ').trim());
      let i = 0; const words = line.textContent.trim().split(/\s+/);
      line.innerHTML = words.map(w => '<span class="fx-w" aria-hidden="true">' + Array.from(w).map(c => '<span class="fx-ch" style="--i:' + (i++) + '">' + c.replace(/[<>&]/g, '') + '</span>').join('') + '</span>').join(' ');
      line.closest('.hero')?.classList.add('fx-split');
    });
  }

  /* ---------- count-up stats ---------- */
  function countUp(scope = document) {
    if (!motion || !vio) return;
    const io = new IntersectionObserver(es => es.forEach(en => {
      if (!en.isIntersecting) return; io.unobserve(en.target);
      const el = en.target, m = el.textContent.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/); if (!m) return;
      const end = parseFloat(m[2]), t0 = performance.now(), dur = 1400;
      const step = t => { const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3); el.textContent = m[1] + Math.round(end * e) + m[3]; if (p < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    }), { threshold: .6 });
    $$('.story__stats dt', scope).forEach(dt => { if (!dt.dataset.cu) { dt.dataset.cu = 1; io.observe(dt); } });
  }

  /* ---------- 3D tilt with glare ---------- */
  const TILT = '.card__media, .tile, .hero__frame, .spot__img, .story__media, .pdp__main, .hdr--logo-tilt .hdr__brand';
  function initTilt() {
    if (!motion || !fine || !body.classList.contains('fx-3d')) return;
    let cur = null;
    const reset = el => { el.classList.remove('is-tilting'); el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); };
    document.addEventListener('pointermove', e => {
      const el = e.target.closest?.(TILT);
      if (cur && cur !== el) { reset(cur); cur = null; }
      if (!el) return;
      cur = el; const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      const max = el.matches('.hero__frame, .pdp__main, .story__media') ? 7 : 11;
      el.classList.add('is-tilting');
      el.style.setProperty('--rx', ((.5 - y) * max).toFixed(2) + 'deg');
      el.style.setProperty('--ry', ((x - .5) * max).toFixed(2) + 'deg');
      el.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
      el.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
    }, { passive: true });
    document.addEventListener('pointerleave', () => { if (cur) { reset(cur); cur = null; } });
  }

  /* ---------- hero pointer depth ---------- */
  function initHeroDepth() {
    if (!motion || !fine) return;
    $$('.hero').forEach(hero => {
      if (hero.dataset.depthInit) return; hero.dataset.depthInit = 1;
      const layers = $$('[data-depth]', hero);
      hero.addEventListener('pointermove', e => {
        const r = hero.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - .5, ny = (e.clientY - r.top) / r.height - .5;
        layers.forEach(l => { const d = parseFloat(l.dataset.depth) || 1; l.style.translate = (nx * d * 18).toFixed(1) + 'px ' + (ny * d * 18).toFixed(1) + 'px'; });
      }, { passive: true });
      hero.addEventListener('pointerleave', () => layers.forEach(l => { l.style.translate = ''; }));
    });
  }

  /* ---------- magnetic buttons ---------- */
  function initMagnetic() {
    if (!motion || !fine) return;
    document.addEventListener('pointermove', e => {
      const b = e.target.closest?.('.btn'); if (!b || b.disabled) return;
      const r = b.getBoundingClientRect();
      b.style.translate = ((e.clientX - r.left - r.width / 2) * .22).toFixed(1) + 'px ' + ((e.clientY - r.top - r.height / 2) * .32).toFixed(1) + 'px';
      if (!b.dataset.mag) { b.dataset.mag = 1; b.addEventListener('pointerleave', () => { b.style.translate = ''; }); }
    }, { passive: true });
  }

  /* ---------- cursor glow ---------- */
  function initGlow() {
    if (!motion || !fine || !body.classList.contains('fx-glow-on')) return;
    const g = document.createElement('div'); g.className = 'fx-glow'; g.setAttribute('aria-hidden', 'true'); body.appendChild(g);
    let tx = innerWidth / 2, ty = innerHeight / 2, x = tx, y = ty, run = false;
    const loop = () => { x += (tx - x) * .12; y += (ty - y) * .12; g.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)'; if (Math.abs(tx - x) + Math.abs(ty - y) > .5) requestAnimationFrame(loop); else run = false; };
    addEventListener('pointermove', e => { tx = e.clientX; ty = e.clientY; g.classList.add('is-on'); if (!run) { run = true; requestAnimationFrame(loop); } }, { passive: true });
    document.addEventListener('pointerleave', () => g.classList.remove('is-on'));
  }

  /* ---------- scroll: parallax + velocity skew ---------- */
  function initScroll() {
    if (!motion) return;
    const root = document.documentElement;
    let last = scrollY, skew = 0, ticking = false;
    const heroMedia = document.querySelector('.hero__media');
    const frame = () => {
      const vh = innerHeight;
      visible.forEach(sec => {
        const r = sec.getBoundingClientRect(); const p = (vh - r.top) / (vh + r.height) - .5;
        const d = sec.firstElementChild; if (!d || !d.classList.contains('fx-decor')) return;
        d.style.setProperty('--px', (p * (d.dataset.side === 'r' ? 260 : -260)).toFixed(1));
        d.style.setProperty('--py', (p * -160).toFixed(1));
      });
      if (heroMedia && scrollY < vh * 1.2) heroMedia.style.translate = '0 ' + (scrollY * .1).toFixed(1) + 'px';
      const v = scrollY - last; last = scrollY;
      skew += (Math.max(-7, Math.min(7, v * .18)) - skew) * .2;
      root.style.setProperty('--fx-skew', skew.toFixed(2) + 'deg');
      if (Math.abs(skew) > .05) requestAnimationFrame(frame); else { root.style.setProperty('--fx-skew', '0deg'); ticking = false; }
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }, { passive: true });
    requestAnimationFrame(frame);
  }


  /* ---------- big headlines tilt toward the cursor ---------- */
  function initTextTilt(scope = document) {
    if (!motion || !fine || !body.classList.contains('fx-text-tilt')) return;
    $$('.hero__title, .pdp__title, .h1', scope).forEach(el => {
      if (el.dataset.tt) return; el.dataset.tt = 1;
      const zone = el.closest('section, .shopify-section') || el.parentElement;
      zone.addEventListener('pointermove', e => {
        const r = el.getBoundingClientRect();
        const nx = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width / 2 + 200)));
        const ny = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height / 2 + 200)));
        el.classList.add('is-ttilt');
        el.style.setProperty('--rx', (-ny * 9).toFixed(2) + 'deg');
        el.style.setProperty('--ry', (nx * 12).toFixed(2) + 'deg');
      }, { passive: true });
      zone.addEventListener('pointerleave', () => { el.classList.remove('is-ttilt'); el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); });
    });
  }

  /* clear reveal stagger delay once revealed so tilt stays snappy */
  document.addEventListener('transitionend', e => { if (e.propertyName === 'opacity' && e.target.classList?.contains('is-in')) e.target.style.transitionDelay = '0ms'; });

  function init(scope) { decorate(scope); splitHero(scope); countUp(scope); initTextTilt(scope); }
  const boot = () => { init(document); initTilt(); initHeroDepth(); initMagnetic(); initGlow(); initScroll(); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  document.addEventListener('shopify:section:load', e => { init(e.target); initHeroDepth(); });
})();
